/**
 * Login — email + password entry against the Cognito user pool.
 * Dark theme, amber accents, Cinzel headings to match the rest of the
 * VTT. Handles two flows in-page:
 *
 *   1. Normal sign-in → redirect to the page the user was trying to reach.
 *   2. First-time login: Cognito returns NEW_PASSWORD_REQUIRED for
 *      admin-created users with temporary passwords. We swap to a
 *      "set permanent password" phase and finish the challenge in place.
 */
import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import type { AuthUser } from '../lib/auth';

interface LocationState {
  from?: { pathname: string };
  notice?: string;
}

type Phase =
  | { kind: 'credentials' }
  | {
      kind: 'newPassword';
      complete: (newPassword: string) => Promise<AuthUser>;
    };

export default function Login() {
  const { user, signIn, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [phase, setPhase] = useState<Phase>({ kind: 'credentials' });
  // Cognito accepts whatever string we hand it as USERNAME and resolves
  // it against the pool's sign-in aliases. The pool is configured with
  // email as a sign-in alias, so we collect email here for UX.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return <p style={{ padding: 'var(--sp-6)' }}>Checking session…</p>;
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const locationState = location.state as LocationState | null;
  const redirectTo = locationState?.from?.pathname ?? '/dashboard';
  const notice = locationState?.notice ?? null;

  async function onSubmitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'success') {
        navigate(redirectTo, { replace: true });
        return;
      }
      // First-time login challenge — hold the in-flight cognito session
      // in component state and swap to the new-password form.
      setPhase({ kind: 'newPassword', complete: result.complete });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitNewPassword(e: FormEvent) {
    e.preventDefault();
    if (phase.kind !== 'newPassword') return;
    setError(null);

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await phase.complete(newPassword);
      // The cognito-identity-js library persists the session once the
      // challenge completes, so refresh() picks it up and fills the
      // AuthContext user. Then we can navigate as normal.
      await refresh();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set new password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        maxWidth: 420,
        margin: '10vh auto',
        padding: 'var(--sp-8)',
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      <h1 style={{ textAlign: 'center' }}>GoobDoob VTT</h1>
      {notice && (
        <p style={{ color: 'var(--active)', textAlign: 'center', marginTop: 0 }}>{notice}</p>
      )}

      {phase.kind === 'credentials' ? (
        <>
          <form onSubmit={onSubmitCredentials} style={{ display: 'grid', gap: 'var(--sp-4)' }}>
            <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
              <span>Email</span>
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={inputStyle}
              />
            </label>
            {error && <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
            <button type="submit" disabled={submitting} style={primaryButton(submitting)}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 'var(--sp-6)' }}>
            <Link to="/forgot-password" style={{ color: 'var(--sheet-a)' }}>
              Forgot password?
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={onSubmitNewPassword} style={{ display: 'grid', gap: 'var(--sp-4)' }}>
          <p style={{ margin: 0, color: 'var(--fg-muted)' }}>
            Welcome! This is your first sign-in — pick a permanent password to replace the
            temporary one.
          </p>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
          </label>
          {error && <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <button type="submit" disabled={submitting} style={primaryButton(submitting)}>
            {submitting ? 'Saving…' : 'Set password and continue'}
          </button>
        </form>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-base)',
  color: 'var(--fg-strong)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontFamily: 'var(--font-body)',
  fontSize: 16,
};

function primaryButton(busy: boolean): React.CSSProperties {
  return {
    padding: 'var(--sp-3) var(--sp-4)',
    background: 'var(--sheet-a)',
    color: 'var(--bg-deep)',
    border: 'none',
    borderRadius: 4,
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: busy ? 'wait' : 'pointer',
  };
}
